import React, { FC, useState, useEffect } from 'react';
import styles from './MaintenanceTriggerForm.module.scss';
import { connect } from 'react-redux';
import { Form, Input, Checkbox, Select, Card } from 'antd';
import Rules from './Rules/Rules';
import { RootState } from '../../../rootReducer';
import { AppDispatch } from '../../../store';
import {
  IdText,
  getStandardDetails,
  ResponseLevel,
  createMaintenanceTrigger,
  updateMaintenanceTrigger,
  cloneMaintenanceTrigger
} from './MaintenanceTriggerForm.slice';
import { AnnotationStandardDetails } from '../../AnnotationManager/AnnotationStandardForm/AnnotationStandardForm.slice';
import { SelectValue } from 'antd/lib/select';
import Header from './Header';
import FormActions from './FormActions';
import { motion } from 'framer-motion';
import { RequiredFields } from './Helpers';
import { prepareFormDataSelector, FormDataInitial } from './formSelector';
const { Option } = Select;

type Props = {
  responseLevels: ResponseLevel[];
  standards: IdText[];
  standard: AnnotationStandardDetails | null;
  formData: FormDataInitial;
  hasDefaultMaintenanceTrigger: boolean;
  isCloning: boolean;
  isFormSubmiting: boolean;
  finished: boolean;
  onGetStandard: (id: string) => void;
  onCreate: (name: string, version: string, defaultCheckbox: boolean, rules: any[]) => void;
  onUpdate: (name: string, version: string, defaultCheckbox: boolean, rules: any[]) => void;
  onClone: (name: string, rules: any[]) => void;
};

export const MaintenanceTriggerForm: FC<Props> = ({
  formData,
  responseLevels,
  standard,
  standards,
  hasDefaultMaintenanceTrigger,
  isCloning,
  isFormSubmiting,
  finished,
  onGetStandard,
  onCreate,
  onUpdate,
  onClone
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [name, setName] = useState(formData.name);
  const [version, setVersion] = useState(formData.version);
  const [isDefault, setIsDefault] = useState(formData.default);
  const [rules, setRules] = useState<any[]>(formData.rules);

  const onStandardSelect = (value: SelectValue) => {
    setIsLoading(true);
    onGetStandard(value as string);
  };

  const onCreateClicked = () => {
    onCreate(name, version, isDefault, rules);
  };

  const onUpdateClicked = () => {
    onUpdate(name, version, isDefault, rules);
  };

  const onCloneClicked = () => {
    const newName = `Copy of ${name}`;
    setName(newName);
    onClone(newName, rules);
  };

  const isValid = () => {
    const requiredPopulated = !rules.some(rule => RequiredFields.some(requiredField => !rule[requiredField]));
    return !!(name && standard && rules.length && requiredPopulated);
  };

  useEffect(() => {
    if (standard) {
      setIsLoading(false);
    }
  }, [standard]);

  useEffect(() => {
    if (formData.isUpdate) {
      onGetStandard(formData.annotationStandardId);
    }
  }, [formData.annotationStandardId, formData.isUpdate, onGetStandard]);

  return (
    <motion.div className={styles.maintenanceTriggerFormContainer}>
      <Card className={styles.card}>
        <Form layout='vertical' style={{ padding: '0 50px' }}>
          <Header isCloning={isCloning} canClone={formData.isUpdate} onClone={onCloneClicked} />
          <Form.Item label='Name' className={styles.antFormItem} required>
            <Input placeholder='Name' value={name} onChange={e => setName(e.target.value)} />
          </Form.Item>
          <Form.Item label='Version' className={styles.antFormItem} required>
            <Input placeholder='Version' value={version} onChange={e => setVersion(e.target.value)} />
          </Form.Item>
          <Form.Item label='Annotation Standard' className={styles.antFormItem} required>
            <Select
              showSearch
              disabled={formData.isUpdate}
              style={{ width: '100%' }}
              placeholder='Please select'
              optionFilterProp='children'
              onChange={onStandardSelect}
              filterOption={true}
              value={standard ? standard.id : undefined}
            >
              {standards.map(type => (
                <Option value={type.id} key={type.id}>
                  {type.text}
                </Option>
              ))}
            </Select>
          </Form.Item>
          {!!standard && !isLoading && (
            <Form.Item className={styles.antFormItem}>
              <Checkbox disabled={hasDefaultMaintenanceTrigger} checked={isDefault} onChange={e => setIsDefault(e.target.checked)}>
                Default
              </Checkbox>
            </Form.Item>
          )}
          <Rules isLoading={isLoading} standard={standard} rules={rules} setRules={setRules} responseLevels={responseLevels} formData={formData} />
          <FormActions
            isLoading={isFormSubmiting}
            isUpdate={formData.isUpdate}
            finished={finished}
            disabled={!isValid()}
            onCreate={onCreateClicked}
            onUpdate={onUpdateClicked}
          />
        </Form>
      </Card>
    </motion.div>
  );
};

const mapStateToProps = (state: RootState) => ({
  standards: state.maintenanceTriggerForm.standards,
  standard: state.maintenanceTriggerForm.standard,
  responseLevels: state.maintenanceTriggerForm.responseLevels,
  formData: prepareFormDataSelector(state),
  hasDefaultMaintenanceTrigger: state.maintenanceTriggerForm.standardHasDefaultTrigger,
  isCloning: state.maintenanceTriggerForm.isCloning,
  isFormSubmiting: state.maintenanceTriggerForm.isLoading,
  finished: state.maintenanceTriggerForm.finished
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  onGetStandard: (id: string) => dispatch(getStandardDetails(id)),
  onCreate: (name: string, version: string, defaultCheckbox: boolean, rules: any[]) =>
    dispatch(createMaintenanceTrigger(name, version, defaultCheckbox, rules)),
  onClone: (name: string, rules: any[]) => dispatch(cloneMaintenanceTrigger(name, rules)),
  onUpdate: (name: string, version: string, defaultCheckbox: boolean, rules: any[]) =>
    dispatch(updateMaintenanceTrigger(name, version, defaultCheckbox, rules))
});

export default connect(mapStateToProps, mapDispatchToProps)(MaintenanceTriggerForm);
